import React from 'react';

import { Chip, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

export type CentralProcessingActivity = {
  name: string;
  description: string;
  processingRole: string;
  dataSubjectCategories: string[];
  personalDataCategories: string[];
  recipientCategories: string[];
  minimumSecurityMeasures: string[];
  purposes: Array<{
    name: string;
    legalBasis: string;
    legalBasisComment: string;
    liaRequired: boolean;
    retentionRule: string;
  }>;
};

type Props = {
  activity: CentralProcessingActivity;
};

const EMPTY_VALUE = '—';

const ProcessingActivitySidePanel: React.FC<Props> = ({ activity }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.apa_side_panel;
  const processingRoleMessages = processing_activities_messages.processing_role as Record<string, string[]>;

  const processingRoleMessage = activity.processingRole ? processingRoleMessages[activity.processingRole] : undefined;
  const processingRoleLabel = processingRoleMessage ? t(processingRoleMessage) : activity.processingRole || EMPTY_VALUE;

  const renderCategoryChips = (items: string[]) => {
    if (items.length === 0) {
      return EMPTY_VALUE;
    }

    return (
      <div className={styles.categoryTagRow}>
        {items.map(item => (
          <Chip key={item} className={styles.categoryChip} label={item} size='small' />
        ))}
      </div>
    );
  };

  return (
    <aside className={styles.sidePanel}>
      <Typography gutterBottom variant='h3'>
        {t(messages.title)}
      </Typography>
      <Typography color='textSecondary' gutterBottom variant='body2'>
        {t(messages.read_only_note)}
      </Typography>
      <div className={styles.fieldList}>
        <div>
          <div className={styles.fieldLabel}>{t(messages.name)}</div>
          <div>{activity.name}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.description)}</div>
          <div className={styles.preLineValue}>{activity.description || EMPTY_VALUE}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.processing_role)}</div>
          <div>{processingRoleLabel}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.data_subjects)}</div>
          <div>{renderCategoryChips(activity.dataSubjectCategories)}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.personal_data)}</div>
          <div>{renderCategoryChips(activity.personalDataCategories)}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.recipients)}</div>
          <div>{renderCategoryChips(activity.recipientCategories)}</div>
        </div>
        <div>
          <div className={styles.fieldLabel}>{t(messages.minimum_security)}</div>
          <div className={styles.preLineValue}>
            {activity.minimumSecurityMeasures.length > 0 ? activity.minimumSecurityMeasures.join(', ') : EMPTY_VALUE}
          </div>
        </div>
      </div>
      {activity.purposes.map(purpose => (
        <div key={purpose.name} className={styles.fieldList} style={{ marginTop: 16 }}>
          <Typography gutterBottom variant='h4'>
            {purpose.name}
          </Typography>
          <div>
            <div className={styles.fieldLabel}>{t(messages.legal_basis)}</div>
            <div>{purpose.legalBasis}</div>
          </div>
          <div>
            <div className={styles.fieldLabel}>{t(messages.legal_basis_comment)}</div>
            <div>{purpose.legalBasisComment || EMPTY_VALUE}</div>
          </div>
          <div>
            <div className={styles.fieldLabel}>{t(messages.lia_required)}</div>
            <div>{purpose.liaRequired ? t(messages.yes) : t(messages.no)}</div>
          </div>
          <div>
            <div className={styles.fieldLabel}>{t(messages.retention_rule)}</div>
            <div>{purpose.retentionRule}</div>
          </div>
        </div>
      ))}
    </aside>
  );
};

export default ProcessingActivitySidePanel;
